import React, { useState, useEffect } from "react";
import axios from "axios";
import AuthHeader from "../../../UI/AuthHeader/AuthHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { Modal } from "../../../UI/Modal/Modal";
import Rules from "../../Rules";
import BubbleFloat from "../../../UI/BubbleFloat/BubbleFloat";
import { checkAuthenticatedUser, startExamSession } from "../../../../utils/client-functions";
import { useErrorSetter } from '../../../../hooks/useUiMessages';
import useExamStartGate from "../../../../hooks/useExamStartGate";
import ExamReadyModal from "../../ExamReadyModal";

// FCSC Recruitment CBT mirrors NCS combined exam flow (custom rules differ only)

const Fcsc = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);

  const isAuth = localStorage?.getItem("token");
  const errorSetter = useErrorSetter();

  const toggleAuthMenu = () => setAuthMenuOpen(!authMenuOpen);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", window.location.pathname);
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

  // Single "subject" metadata
  const fcscExamMeta = {
    subject: "Federal Civil Service Commission (FCSC) Recruitment CBT",
    timeAlloted: 2700,
    description: `This mock simulates the FCSC Recruitment Computer-Based Test (CBT) under realistic practice conditions.\n\nConduct Rules (practice simulation):\n- Online CBT with identity/webcam verification.\n- Use a laptop/desktop device only (no mobile).\n- Maintain single-window focus; malpractice may terminate session.\n- A short practice reminder may precede the exam.\n\nBlueprint (practice approximation):\n- 50 MCQs spanning: English Language & Verbal, Quantitative & Logical Reasoning, Public Service / General Knowledge.\n- Balanced difficulty and realistic timing.\n\nDisclaimer: This mock is an informed approximation for training speed, accuracy, and disciplined exam conduct.`,
  };

  const closeModalsAndReset = () => {
    navigate('/user/dashboard/mock');
  };

  const handleStartExam = async () => {
    try {
      await checkAuthenticatedUser();
    } catch (error) {
      errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", window.location.pathname);
            return navigate("/popin");
    }

    setLoading(true);
    setProgressStatus("Gathering Exam Details...");
    setProgressPercentage(5);

    const payload = {
      subject: fcscExamMeta.subject,
      timeAlloted: fcscExamMeta.timeAlloted,
      description: fcscExamMeta.description,
      examName: "FCSC",
    };

    try {
      await startExamSession({
        payload,
        tokenOverride: isAuth,
        onOngoing: (details) => { setLoading(false); handleOngoing(details); },
        onJobCreated: () => {},
        onProgress: (status, pct) => { setProgressStatus(status || 'Processing...'); setProgressPercentage(pct || 10); },
        onDone: (e) => { try { handleDoneEvent(e); } catch(_){}; setLoading(false); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        setLoading,
        setProgressStatus,
        setProgressPercentage,
      });
    } catch (err) {
      setLoading(false);
      errorSetter(err?.response?.data?.message || err?.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
      if (err?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div className="auth-container">
      <BubbleFloat />
      <div className="auth-container-inner content">
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleAuthMenu}
          headerText="FCSC MOCK"
        />
      </div>

      {!examReady && (
        <Rules
          examName={"Federal Civil Service Commission Recruitment CBT"}
          closeModalsAndReset={closeModalsAndReset}
          subject={fcscExamMeta}
          handleStartExam={handleStartExam}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}
      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel="FCSC"
        />
      )}
    </div>
  );
};

export default Fcsc;

